import { Link, useMatches } from '@tanstack/react-router';
import { Fragment } from 'react';

/**
 * Breadcrumbs — derived from the active route chain via TanStack Router's
 * `useMatches()`. Each route declares its label through `staticData.breadcrumb`
 * (see `route-tree.tsx`):
 *
 *   • `string` — static label, shown verbatim
 *   • `(match) => string` — dynamic label, receives the route match (useful
 *     for `/admin/users/$id` once we wire a loader; for now it returns a
 *     fixed string).
 *   • `undefined` — segment is skipped (e.g. the root `/` redirect route).
 *
 * The last crumb is rendered as plain text (current page); earlier crumbs
 * are `<Link>`s. The "Domů" crumb is always prepended and points at
 * `/` so an authenticated user has a one-click escape hatch from any deep
 * page back to the dlaždicový rozcestník (HomePage, #72025).
 *
 * Path-based parent inference
 * ───────────────────────────
 * Our routes are currently registered flat under `rootRoute` (e.g.
 * `/admin/users` and `/admin/users/$id` are siblings, not parent+child),
 * so `useMatches()` for `/admin/users/new` only returns the leaf match.
 * To still present a meaningful hierarchy ("Domů / Uživatelé / Nový
 * uživatel") we synthesize missing intermediates from a small prefix map
 * — adding a new section means adding one line below. When the route tree
 * is migrated to nested children this map can shrink to zero.
 */

const PATH_PARENTS: ReadonlyArray<{ prefix: string; label: string; to: string }> = [
  { prefix: '/orders/', label: 'Zakázky', to: '/orders' },
  { prefix: '/admin/users/', label: 'Uživatelé', to: '/admin/users' },
];

type BreadcrumbValue = string | ((match: BreadcrumbMatch) => string) | undefined;

interface BreadcrumbMatch {
  pathname: string;
  params: Record<string, string>;
  routeId: string;
}

interface RouteStaticData {
  breadcrumb?: BreadcrumbValue;
}

export function Breadcrumbs() {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];

  const crumbs: Array<{ label: string; to?: string }> = [{ label: 'Domů', to: '/' }];

  // Inject synthetic parent crumbs based on leaf pathname prefix.
  if (leaf) {
    for (const entry of PATH_PARENTS) {
      if (leaf.pathname.startsWith(entry.prefix) && leaf.pathname !== entry.to) {
        crumbs.push({ label: entry.label, to: entry.to });
      }
    }
  }

  for (const match of matches) {
    const staticData = match.staticData as RouteStaticData | undefined;
    const value = staticData?.breadcrumb;
    if (value === undefined) continue;

    const label =
      typeof value === 'function'
        ? value({
            pathname: match.pathname,
            params: match.params as Record<string, string>,
            routeId: match.routeId,
          })
        : value;

    if (!label) continue;
    // Avoid duplicating a crumb already pushed by the prefix map.
    if (crumbs.some((c) => c.to === match.pathname)) continue;
    crumbs.push({ label, to: match.pathname });
  }

  // De-dup the leading "Domů" entry when we're already on the home page so
  // the user doesn't see "Domů / Přehled" — just "Přehled".
  if (crumbs.length >= 2 && crumbs[0]?.to === crumbs[1]?.to) {
    crumbs.shift();
  }

  // Don't render a single-item breadcrumb (just the page itself adds no value).
  if (crumbs.length <= 1) return null;

  return (
    <nav aria-label="Drobečková navigace" className="text-sm text-slate-500">
      <ol className="flex flex-wrap items-center gap-1">
        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <Fragment key={`${crumb.to ?? 'leaf'}-${idx}`}>
              <li>
                {isLast || !crumb.to ? (
                  <span aria-current={isLast ? 'page' : undefined} className="text-slate-900">
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.to}
                    className="rounded text-slate-500 hover:text-slate-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="text-slate-300">
                  /
                </li>
              ) : null}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
